import { observer } from 'mobx-react-lite'
import clsx from 'clsx'
import { useMemo, useRef } from 'react'

import { store } from '@/src/stores/store'
import ButtonMove from '../buttons/ButtonMove'
import ControlHintArrow from './ControlHintArrow'
import ControlHintHand from './ControlHintHand'
import ControlHintShine from './ControlHintShine'
import { useRunState } from '@/src/game/provider/useRunState'
import { motion, Variants } from 'motion/react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { vibrate } from '@/src/lib/vibration'

type GameControlsProps = {
    visible: boolean
    onJump: () => void
    onMoveLeft: () => void
    onMoveRight: () => void
}

const GameControls = observer(function GameControls({
    visible,
    onJump,
    onMoveLeft,
    onMoveRight,
}: GameControlsProps) {

    const runState = useRunState()

    const handleSnap = () => {
        if (runState.snapshot.batteryCount <= 0) return
        vibrate(300)
        window.dispatchEvent(new Event('snap-trigger'))
    }

    const isStele = useSteleBreakpoint()
    const prevVisibleRef = useRef(visible)
    const slideInKeyRef = useRef(0)

    if (visible && !prevVisibleRef.current) {
        slideInKeyRef.current += 1
    }
    prevVisibleRef.current = visible

    const gameControlVariants: Variants = useMemo(
        () => ({
            hidden: {
                y: isStele ? 5000 : 350,
            },
            visible: {
                y: 0,
                transition: {
                    type: 'spring',
                    stiffness: 400,
                    damping: 22,
                    mass: 0.9,
                },
            },
        }),
        [isStele],
    )

    return (
        <motion.div
            key={slideInKeyRef.current}
            initial='hidden'
            animate={visible ? 'visible' : 'hidden'}
            variants={gameControlVariants}
            className={clsx(
                'z-20 absolute inset-x-0 flex justify-between',
                'bottom-4.5 ',
                'stele:bottom-40 stele:px-12',
                visible ? 'pointer-events-auto' : 'pointer-events-none',
            )}
            style={{ touchAction: 'none' }}
        >
            <div className='inline-flex relative w-full'>
                <div className='z-20 absolute inset-0 pointer-events-none'>
                    {store.isTutorialHintDisplayed('snap') ? (
                        <ControlHintArrow side='down' />
                    ) : null}
                    {store.isTutorialHintDisplayed('left') ? (
                        <ControlHintHand type='left' />
                    ) : null}
                    {store.isTutorialHintDisplayed('snap') ? (
                        <ControlHintHand type='snap' />
                    ) : null}
                </div>
                <div className='z-0 absolute inset-0 pointer-events-none'>
                    {store.isTutorialHintDisplayed('left') ? (
                        <ControlHintShine type='left' />
                    ) : null}
                    {store.isTutorialHintDisplayed('snap') ? (
                        <ControlHintShine type='snap' />
                    ) : null}
                    {store.isTutorialHintDisplayed('right') ? (
                        <ControlHintShine type='right' />
                    ) : null}
                    {store.isTutorialHintDisplayed('jump') ? (
                        <ControlHintShine type='jump' />
                    ) : null}
                </div>
                <div className='z-10 relative w-full'>
                    <ButtonMove
                        onPointerDownRight={onMoveRight}
                        onJump={onJump}
                        onPointerDownLeft={onMoveLeft}
                        onSnap={handleSnap}
                        snapDisabled={runState.snapshot.batteryCount <= 0 || runState.phase !== 'running'}
                        batteryCount={runState.snapshot.batteryCount}
                    />
                </div>
                <div className='z-20 absolute inset-0 pointer-events-none'>
                    {store.isTutorialHintDisplayed('right') ? (
                        <ControlHintHand type='right' />
                    ) : null}
                    {store.isTutorialHintDisplayed('jump') ? (
                        <ControlHintHand type='jump' />
                    ) : null}
                </div>
            </div>
        </motion.div>
    )
})

export default GameControls
